import { z } from 'zod';

// Token schema for MSAL authentication data
export const TokenSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().optional(),
  expiresAt: z.number().positive(),
  scopes: z.array(z.string()),
  idToken: z.string().optional(),
  tokenType: z.string().default('Bearer')
});

// User schema for profile information
export const UserSchema = z.object({
  id: z.string().min(1),
  name: z.string().optional(),
  email: z.string().email(),
  upn: z.string().optional(),
  tenantId: z.string().optional(),
  accountType: z.enum(['office365', 'outlookCom', 'other']).optional(),
  loginHint: z.string().optional()
});

// Authentication state schema
export const AuthStateSchema = z.object({
  isAuthenticated: z.boolean(),
  isInitialized: z.boolean(),
  isLoading: z.boolean(),
  tokens: TokenSchema.optional(),
  user: UserSchema.optional(),
  loginHint: z.string().optional(),
  lastRefresh: z.number().optional(),
  error: z.string().optional()
});

// UI state schema
export const UIStateSchema = z.object({
  currentRoute: z.string().default('/'),
  isLoading: z.boolean().default(false),
  errors: z.array(z.string()).default([])
});

// Complete store state schema
export const StoreStateSchema = z.object({
  auth: AuthStateSchema,
  ui: UIStateSchema
});

// Export TypeScript types
export type Token = z.infer<typeof TokenSchema>;
export type User = z.infer<typeof UserSchema>;
export type AuthState = z.infer<typeof AuthStateSchema>;
export type UIState = z.infer<typeof UIStateSchema>;
export type StoreState = z.infer<typeof StoreStateSchema>;

// Helper validation functions
export const validateToken = (data: unknown): Token => {
  return TokenSchema.parse(data);
};

export const validateUser = (data: unknown): User => {
  return UserSchema.parse(data);
};

export const createInitialState = (): StoreState => {
  return StoreStateSchema.parse({
    auth: {
      isAuthenticated: false,
      isInitialized: false,
      isLoading: false,
      tokens: undefined,
      user: undefined,
      loginHint: undefined,
      lastRefresh: undefined,
      error: undefined
    },
    ui: {
      currentRoute: '/',
      isLoading: false,
      errors: []
    }
  });
}; 